import { useQuery } from "@tanstack/react-query";
import { Check, Loader2, Trophy, X } from "lucide-react";
import { penaltyApi } from "@/api/penalty";
import { useScope } from "@/app/useScope";
import { TeamFlag } from "@/components/TeamFlag";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export function PenaltyPicksModal({ matchId, onClose }: { matchId: string | null; onClose: () => void }) {
  const scope = useScope();
  const q = useQuery({
    queryKey: ["penaltyPicks", matchId, scope.leagueId, scope.tournamentId],
    queryFn: () => penaltyApi.picks(matchId!, scope),
    enabled: !!matchId,
  });

  const total = q.data?.picks.length ?? 0;
  const correct = q.data?.picks.filter((p) => p.correct).length ?? 0;

  return (
    <Dialog open={!!matchId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">🥅 Mások 11-es tippjei</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {q.isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {q.data && (
            <>
              <div className="flex items-center justify-center gap-2 rounded-lg bg-success/10 py-2 text-sm font-medium text-success">
                <Trophy className="size-4" /> Párbajgyőztes:
                <TeamFlag name={q.data.winnerName} flag={q.data.winnerFlag} className="size-5" />
                {q.data.winnerName}
              </div>
              <p className="text-center text-xs text-muted-foreground">
                {correct} / {total} tippelő találta el
              </p>
              <ul className="divide-y divide-border/60">
                {q.data.picks.map((p, i) => (
                  <li key={i} className="flex items-center gap-2 py-1.5 text-sm">
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    <TeamFlag name={p.teamName} flag={p.flag} className="size-4" />
                    <span className="w-24 truncate text-muted-foreground">{p.teamName}</span>
                    <span className={cn("grid size-5 place-items-center rounded-full", p.correct ? "bg-warning/20 text-warning" : "bg-destructive/15 text-destructive")}>
                      {p.correct ? <Check className="size-3.5" /> : <X className="size-3.5" />}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
